const formatters = {
  percent: (fractionDigits: number = 2) => {
    return new Intl.NumberFormat('en', {
      style: 'decimal', // avoid letter %
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  },
  paddedPercent: new Intl.NumberFormat('en', {
    style: 'decimal', // avoid letter %
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    minimumIntegerDigits: 2,
  }),
  units: (unit: string) =>
    new Intl.NumberFormat('en', {
      style: 'unit',
      unit: unit,
      notation: 'compact',
      unitDisplay: 'narrow',
    }),
};

export type CurrencyDisplayVariant = 'symbol' | 'code';

export const numbers = {
  formatPrice: (
    value: number,
    currency?: Intl.NumberFormatOptions['currency'] | null,
    variant: CurrencyDisplayVariant = 'symbol',
  ): string => {
    const formatter = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency ?? 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currencyDisplay: variant,
    });

    const parts = formatter.formatToParts(value);

    return parts.reduce((acc, cur) => {
      let chunk: string;

      switch (cur.type) {
        case 'currency':
          chunk = currency === null ? '' : `${cur.value} `;
          break;
        case 'group':
          chunk = ' ';
          break;
        // integer
        // decimal
        // fraction
        default:
          chunk = cur.value;
      }

      return `${acc}${chunk}`;
    }, '');
  },

  formatPercent: ({
    value,
    withSign,
    fractionDigits,
    padLeft,
  }: {
    value: number;
    withSign?: boolean;
    fractionDigits?: number;
    padLeft?: boolean;
  }): string => {
    const res = padLeft
      ? formatters.paddedPercent.format(value)
      : formatters.percent(fractionDigits).format(value);
    if (withSign) return `${res}%`;
    return res;
  },

  formatUnits: (value: number, units: string = 'kilobyte'): string => {
    return formatters.units(units).format(value);
  },

  formatMemoryFromBytes: (bytes: number) => {
    return formatters.units('byte').format(bytes);
  },

  formatDay: (value: number) =>
    new Intl.NumberFormat('en', { style: 'unit', unit: 'day' }).format(value),

  formatShortValue: (value: number) => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toFixed(0);
  },
};
